import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from "./constants";

// Beaker/Flask SVG icon for Free Lab card
const BeakerIcon: React.FC = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 6V22L8 38C7.37 39.04 8.1 40.4 9.33 40.4H38.67C39.9 40.4 40.63 39.04 40 38L30 22V6"
      stroke={HOLOLAB_COLORS.precisionBlue}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 6H33"
      stroke={HOLOLAB_COLORS.precisionBlue}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="14" cy="33" r="2.5" fill={HOLOLAB_COLORS.iceBlue} opacity="0.8" />
    <circle cx="22" cy="36" r="1.5" fill={HOLOLAB_COLORS.iceBlue} opacity="0.6" />
    <circle cx="30" cy="31" r="2" fill={HOLOLAB_COLORS.iceBlue} opacity="0.7" />
  </svg>
);

// Clipboard/Guided icon for Guided Experiment card
const GuidedIcon: React.FC = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="10"
      y="8"
      width="28"
      height="34"
      rx="3"
      stroke={HOLOLAB_COLORS.precisionBlue}
      strokeWidth="2"
    />
    <path
      d="M18 8V10C18 11.1 18.9 12 20 12H28C29.1 12 30 11.1 30 10V8"
      stroke={HOLOLAB_COLORS.precisionBlue}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M16 20H32" stroke={HOLOLAB_COLORS.precisionBlue} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M16 26H32" stroke={HOLOLAB_COLORS.precisionBlue} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M16 32H26" stroke={HOLOLAB_COLORS.precisionBlue} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="14" cy="20" r="1.5" fill={HOLOLAB_COLORS.precisionBlue} />
    <circle cx="14" cy="26" r="1.5" fill={HOLOLAB_COLORS.precisionBlue} />
    <circle cx="14" cy="32" r="1.5" fill={HOLOLAB_COLORS.precisionBlue} />
  </svg>
);

// Individual experiment option card
interface ExperimentCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  buttonLabel: string;
  onAction: () => void;
  children?: React.ReactNode;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({
  icon,
  title,
  subtitle,
  description,
  buttonLabel,
  onAction,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: HOLOLAB_COLORS.labWhite,
        border: `1px solid ${isHovered ? "rgba(46, 92, 138, 0.25)" : "rgba(10, 25, 41, 0.10)"}`,
        borderRadius: "12px",
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        flex: "1 1 320px",
        minWidth: "280px",
        maxWidth: "480px",
        boxShadow: isHovered
          ? "0 8px 32px rgba(10, 25, 41, 0.12)"
          : "0 2px 8px rgba(10, 25, 41, 0.06)",
        transform: isHovered ? "translateY(-2px)" : "translateY(0)",
        transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "default",
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: "72px",
          height: "72px",
          borderRadius: "12px",
          background: `rgba(46, 92, 138, 0.06)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(46, 92, 138, 0.12)",
        }}
      >
        {icon}
      </div>

      {/* Text content */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <p
          style={{
            fontFamily: HOLOLAB_FONTS.mono,
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: HOLOLAB_COLORS.precisionBlue,
            margin: 0,
          }}
        >
          {subtitle}
        </p>
        <h2
          style={{
            fontFamily: HOLOLAB_FONTS.heading,
            fontSize: "24px",
            fontWeight: 700,
            color: HOLOLAB_COLORS.deepBlue,
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {title}
        </h2>
        <p
          style={{
            fontFamily: HOLOLAB_FONTS.body,
            fontSize: "15px",
            fontWeight: 400,
            color: `rgba(10, 25, 41, 0.65)`,
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      </div>

      {/* Slot for sub-cards or extra content */}
      {children && <div style={{ flex: 1 }}>{children}</div>}

      {/* CTA Button */}
      <button
        onClick={onAction}
        onKeyPress={(e) => e.key === "Enter" && onAction()}
        aria-label={buttonLabel}
        style={{
          background: HOLOLAB_COLORS.precisionBlue,
          color: HOLOLAB_COLORS.labWhite,
          border: "none",
          borderRadius: "8px",
          padding: "14px 24px",
          fontFamily: HOLOLAB_FONTS.body,
          fontSize: "15px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1)",
          alignSelf: "flex-start",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.opacity = "0.85";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.opacity = "1";
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

// Sub-item card shown inside the Guided Experiment card
interface ExperimentSubCardProps {
  experimentId: string;
  title: string;
  subtitle: string;
  equation: string;
  available: boolean;
  onClick: () => void;
}

const ExperimentSubCard: React.FC<ExperimentSubCardProps> = ({
  experimentId, title, subtitle, equation, available, onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={available ? onClick : undefined}
      onMouseEnter={() => available && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered ? "rgba(46,92,138,0.05)" : HOLOLAB_COLORS.iceBlue,
        border: `1px solid ${isHovered ? "rgba(46,92,138,0.3)" : "rgba(46, 92, 138, 0.15)"}`,
        borderRadius: "8px",
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        cursor: available ? "pointer" : "default",
        transition: "all 150ms cubic-bezier(0.4,0,0.2,1)",
        transform: isHovered ? "translateY(-1px)" : "translateY(0)",
        boxShadow: isHovered ? "0 4px 12px rgba(10,25,41,0.08)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p
          style={{
            fontFamily: HOLOLAB_FONTS.mono,
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: available ? HOLOLAB_COLORS.precisionBlue : "rgba(10,25,41,0.35)",
            margin: 0,
            opacity: 0.9,
          }}
        >
          {available ? "Available Now" : "Coming Soon"}
        </p>
        {available && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={HOLOLAB_COLORS.precisionBlue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: isHovered ? 1 : 0.4, transition: "opacity 150ms" }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        )}
      </div>
      <p
        style={{
          fontFamily: HOLOLAB_FONTS.body,
          fontSize: "14px",
          fontWeight: 600,
          color: HOLOLAB_COLORS.deepBlue,
          margin: 0,
        }}
      >
        {experimentId} — {title}
      </p>
      <p
        style={{
          fontFamily: HOLOLAB_FONTS.body,
          fontSize: "12px",
          fontWeight: 400,
          color: `rgba(10, 25, 41, 0.65)`,
          margin: 0,
        }}
      >
        {subtitle}
      </p>
      <p
        style={{
          fontFamily: HOLOLAB_FONTS.mono,
          fontSize: "13px",
          fontWeight: 500,
          color: HOLOLAB_COLORS.precisionBlue,
          margin: 0,
          letterSpacing: "0.02em",
        }}
      >
        {equation}
      </p>
    </div>
  );
};

// Main ExperimentSelect component
const ExperimentSelect: React.FC = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger fade-in after mount
    const timer = setTimeout(() => setIsLoaded(true), 20);
    return () => clearTimeout(timer);
  }, []);

  const handleFreeLab = () => {
    navigate("/play");
  };

  const handleGuidedExperiment = () => {
    navigate("/play/experiment/zinc-h2so4");
  };

  const handleTitrationExperiment = () => {
    navigate("/play/experiment/titration-hcl-naoh");
  };

  const handleNaphthaleneExperiment = () => {
    navigate("/play/experiment/naphthalene");
  };

  const handleSolubilityExperiment = () => {
    navigate("/play/experiment/solubility");
  };

  const handleMagnesiumOxideExperiment = () => {
    navigate("/play/experiment/magnesium-oxide");
  };

  const handleCopperOxideExperiment = () => {
    navigate("/play/experiment/copper-oxide");
  };

  const handleBalancedEquations = () => {
    navigate("/play/experiment/balanced-equations");
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${HOLOLAB_COLORS.deepBlue} 0%, #0a1628 50%, #0d1a2d 100%)`,
        display: "flex",
        flexDirection: "column",
        fontFamily: HOLOLAB_FONTS.body,
        overflowY: "auto",
        // Subtle grid overlay via pseudo-element workaround: use a background-image
        backgroundImage: `
          linear-gradient(135deg, ${HOLOLAB_COLORS.deepBlue} 0%, #0a1628 50%, #0d1a2d 100%)
        `,
      }}
    >
      {/* Subtle grid overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          pointerEvents: "none",
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* Page content */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          padding: "32px 24px 48px",
          boxSizing: "border-box",
          maxWidth: "1100px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {/* Top Navigation Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "48px",
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(-8px)",
            transition: "opacity 350ms cubic-bezier(0.4, 0, 0.2, 1), transform 350ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Back button */}
          <button
            onClick={handleBack}
            onKeyPress={(e) => e.key === "Enter" && handleBack()}
            aria-label="Back to landing page"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "transparent",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "8px",
              padding: "8px 16px",
              color: `rgba(232, 244, 248, 0.8)`,
              fontFamily: HOLOLAB_FONTS.body,
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.borderColor = "rgba(46, 92, 138, 0.6)";
              btn.style.color = HOLOLAB_COLORS.labWhite;
            }}
            onMouseLeave={(e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.borderColor = "rgba(255, 255, 255, 0.15)";
              btn.style.color = "rgba(232, 244, 248, 0.8)";
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {/* HoloLab brand mark */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                backgroundColor: `rgba(46, 92, 138, 0.2)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "3px",
                }}
              >
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "2px",
                      backgroundColor: i === 0 ? HOLOLAB_COLORS.precisionBlue : HOLOLAB_COLORS.iceBlue,
                      opacity: i === 0 ? 1 : 0.5,
                    }}
                  />
                ))}
              </div>
            </div>
            <span
              style={{
                fontFamily: HOLOLAB_FONTS.heading,
                fontSize: "17px",
                fontWeight: 600,
                color: HOLOLAB_COLORS.labWhite,
                letterSpacing: "-0.01em",
              }}
            >
              HoloLab
            </span>
          </div>
        </div>

        {/* Page Header */}
        <div
          style={{
            marginBottom: "48px",
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 400ms 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 400ms 100ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <p
            style={{
              fontFamily: HOLOLAB_FONTS.mono,
              fontSize: "12px",
              fontWeight: 500,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: HOLOLAB_COLORS.iceBlue,
              opacity: 0.6,
              margin: "0 0 12px",
            }}
          >
            Choose your mode
          </p>
          <h1
            style={{
              fontFamily: HOLOLAB_FONTS.heading,
              fontSize: "clamp(28px, 5vw, 42px)",
              fontWeight: 300,
              color: HOLOLAB_COLORS.labWhite,
              margin: 0,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            Select an Experiment
          </h1>
        </div>

        {/* Cards Row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "24px",
            alignItems: "flex-start",
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 500ms 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Card 1: Free Lab */}
          <ExperimentCard
            icon={<BeakerIcon />}
            subtitle="Makmal Bebas"
            title="Free Lab"
            description="Explore elements freely. Combine any elements with hand gestures to discover compounds."
            buttonLabel="Enter Free Lab"
            onAction={handleFreeLab}
          />

          {/* Card 2: Guided Experiment */}
          <ExperimentCard
            icon={<GuidedIcon />}
            subtitle="Eksperimen Berpandu"
            title="Guided Experiment"
            description="Follow step-by-step procedures for real chemistry experiments with data collection."
            buttonLabel="Start Experiment A"
            onAction={handleGuidedExperiment}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <ExperimentSubCard
                experimentId="Experiment A"
                title="Gas Collection"
                subtitle="Reaction between Zinc and Sulphuric Acid"
                equation="Zn + H₂SO₄ → ZnSO₄ + H₂"
                available={true}
                onClick={handleGuidedExperiment}
              />
              <ExperimentSubCard
                experimentId="Experiment B"
                title="Acid-Base Titration"
                subtitle="Determine concentration of HCl with NaOH"
                equation="HCl + NaOH → NaCl + H₂O"
                available={true}
                onClick={handleTitrationExperiment}
              />
              <ExperimentSubCard
                experimentId="Activity 2.2"
                title="Phase Change"
                subtitle="Melting and Freezing Point of Naphthalene"
                equation="C₁₀H₈ (s) ⇌ C₁₀H₈ (l)"
                available={true}
                onClick={handleNaphthaleneExperiment}
              />
              <ExperimentSubCard
                experimentId="Activity 2.3"
                title="Solubility vs Temperature"
                subtitle="Effect of temperature on solubility of salt"
                equation="NaCl(s) ⇌ Na⁺(aq) + Cl⁻(aq)"
                available={true}
                onClick={handleSolubilityExperiment}
              />
              <ExperimentSubCard
                experimentId="Activity 3.12"
                title="Empirical Formula"
                subtitle="Combustion of Magnesium in Air"
                equation="2Mg + O₂ → 2MgO"
                available={true}
                onClick={handleMagnesiumOxideExperiment}
              />
              <ExperimentSubCard
                experimentId="Activity 3.13"
                title="Empirical Formula of CuO"
                subtitle="Reduction of Copper(II) Oxide by Hydrogen"
                equation="CuO + H₂ → Cu + H₂O"
                available={true}
                onClick={handleCopperOxideExperiment}
              />
              <ExperimentSubCard
                experimentId="Activity 3.18"
                title="Balanced Chemical Equations"
                subtitle="Observe three reactions and write their equations"
                equation="CuCO₃ → CuO + CO₂  |  HCl + NH₃ → NH₄Cl  |  Pb(NO₃)₂ + 2KI → PbI₂ + 2KNO₃"
                available={true}
                onClick={handleBalancedEquations}
              />
            </div>
          </ExperimentCard>
        </div>

        {/* Bottom accent */}
        <div
          style={{
            marginTop: "auto",
            paddingTop: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            opacity: isLoaded ? 0.5 : 0,
            transition: "opacity 600ms 400ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "1px",
              backgroundColor: `rgba(232, 244, 248, 0.3)`,
            }}
          />
          <span
            style={{
              fontFamily: HOLOLAB_FONTS.mono,
              fontSize: "11px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: HOLOLAB_COLORS.iceBlue,
            }}
          >
            Scientific Precision
          </span>
          <div
            style={{
              width: "48px",
              height: "1px",
              backgroundColor: `rgba(232, 244, 248, 0.3)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ExperimentSelect;
